import { useState, useEffect, useContext, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import UserContext from '../../store/user-context';
import ConvContext from '../../store/conversation-context';

import useFetch from '../../hooks/use-fetch';

import Preteach from './Conversation/Preteach';
import WholeConversation from './Conversation/WholeConversation';
import ParrotBox from '../UI/ParrotBox';
import Loader from '../UI/Loader';
import SpeechRecognition from './Conversation/SpeechRecognition';
import ButtonAudioPlayer from './Conversation/ButtonAudioPlayer';
import MainAudioPlayer from './Conversation/MainAudioPlayer';
import {
  fetchAudio,
  fetchNpcAudio,
} from '../Helpers/Conversation/ConversationInit';

import { useSpeechSDK } from '../Helpers/Conversation/SpeechDetection';
import useAnalyticsEventTracker from '../../hooks/use-analytics';

import parrot from '../../assets/images/parrot-cutout-bitesize.png'

const Conversation = () => {
  const {t: translate } = useTranslation();
  //GA vars
  const gaEventTracker = useAnalyticsEventTracker('Scene Selection');

  //Router vars
  const params = useParams();

  //User context
  const { user, setUser } = useContext(UserContext);

  //Redux vars
  const dispatch = useDispatch();
  let storeSceneId = useSelector((state) => state.currentSceneID);
  let sceneID = storeSceneId !== '' ? storeSceneId : params.scene_id; //Most important var here!
  const currentLesson = useSelector((state) => state.currentLesson);
  const { apiUrl, speechKey, speechRegion } = window['appConfig'];
  const isTutorial = params.location === 'tutorial' ? true : false;
  // const gaEventTracker = useAnalyticsEventTracker('Scene Selection');

  //Ensure sceneID AND lesson are set in redux store!
  if (!sceneID || !currentLesson) {
    dispatch({
      type: 'startLesson',
      payload: { sceneID: params.scene_id, lesson: params.lesson },
    });
  }

  //TODO: tart this up
  if (!sceneID) {
    return (
      <section className="section section__conversation">
        <div className="container">
          <div>Scene ID not provided, please go back and try again.</div>
        </div>
      </section>
    );
  }

  //TODO: add check to see if we already have all data
  // const storeLocation = useSelector((state) => state.location);
  // const npcAudioBlobs = useSelector((state)=> state.npcAudioBlobs);
  // const audioBlobs = useSelector((state)=> state.audioBlobs);

  //State vars
  const [error, setError] = useState(false); //TODO: use global context to allow nicer errors?
  const { data: token, initFunc: getToken } = useFetch();
  const [isFetching, setIsFetching] = useState(true);
  const [progressValue, setProgressValue] = useState({ value: [], length: 0 });
  const [activeScreen, setActiveScreen] = useState('landing');

  //Fetch Vars
  const { data, error: fetchErrors, initFunc: callFetch } = useFetch();
  const {
    data: audioData,
    error: audioError,
    initFunc: preloadPreteachAudio,
  } = useFetch();
  const { initFunc: initProgressFetch } = useFetch();

  //Component vars
  // const canSkip = (parseInt(lesson) !== 1 && activeScreen === 'landing') ? true : false;
  const canSkip = true;
  const conversationUrl = `${apiUrl}/scene/${sceneID}`;
  const location = params.location;
  const lesson = params.lesson;

  dispatch({
    type: 'setLocation',
    payload: { location: location },
  });

  //Conversation vars
  const [convContext, setConvContext] = useState({});
  const conversationValue = useMemo(
    () => ({ convContext, setConvContext }),
    [convContext, setConvContext],
  );
  // const [sentences, setsentences] = useState([]);

  // const landingLink = `/conversation/${location}`;

  //Preteach vars
  // const [preteachData, setPreteachData] = useState([]);

  const completePreteach = () => {
    dispatch({
      type: 'deactivatePreteach',
    });

    dispatch({
      type: 'playNpcAudio',
    });

    if (useSpeechSDK()) {
      //Gets around Safaris autoplay policy
      setTimeout(function () {
        document.getElementById('main_audio_player').load();
        document.getElementById('main_audio_player').play();
      }, 100);
    }

    setActiveScreen('conversation');
  };

  const returnToStart = () => {
    dispatch({
      type: 'resetActiveSection',
    });

    setActiveScreen('landing');
  };

  const npcPlayerReady = () => {
    setIsFetching(false);
  };

  const updateUserProgress = (preteach = false) => {
    let fetchData = new URLSearchParams();
    fetchData.append('lesson', currentLesson);
    if (preteach === true) {
      fetchData.append('preteach', true);
    }

    const fetchOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: fetchData,
    };

    const progressUrl = `${apiUrl}/progress/${sceneID}/started`;

    initProgressFetch({ url: progressUrl, options: fetchOptions });
  };

  useEffect(() => {
    //Ensure active section is always reset to 0 when conversation is loaded
    dispatch({
      type: 'resetActiveSection',
    });

    let mounted = true;
    if (error) {
      mounted = false;
    }
    if (mounted) {
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
      setIsFetching(true);
      callFetch({ url: conversationUrl, options: fetchOptions });
    }

    //Show return to landing page button
    if (user.showLandingLink !== true && isFetching) {
      // console.log('set landing link')
      setUser((prevState) => ({
        ...prevState,
        showLandingLink: true,
      }));
    }

    if (useSpeechSDK()) {
      //Microsoft SDK token
      const tokenUrl = `https://${speechRegion}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': speechKey,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };

      getToken({ url: tokenUrl, options: fetchOptions });
    }

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    async function loadData(data) {
      try {
        const sentences = data.sentences;
        if (!sentences) throw 'Sentences could not be found';
        // const experience = data.experience
        const audios = [];
        const npcAudio = [];

        for (let i = 0; i < sentences.length; i++) {
          let npc = await fetchNpcAudio(sentences[i], sceneID);
          // console.log(`got npc index ${i}`)
          let splits = await fetchAudio(sentences[i], sceneID);
          // console.log(`got splits for index ${i}`)

          if (npc === null || npc === undefined) {
            if (
              confirm(
                'Error encountered loading the NPC part, we need to reload the page',
              )
            ) {
              window.location.reload();
            }
          }

          if (
            Object.values(splits).includes(null) ||
            Object.values(splits).includes(null)
          ) {
            if (
              confirm(
                'Error encountered loading the Player splits, we need to reload the page',
              )
            ) {
              window.location.reload();
            }
          }

          npcAudio.push(npc);
          audios.push(splits);

          //Loader screen values
          let progressArr = [npc, splits];
          setProgressValue((prevState) => ({
            ...prevState,
            value: [...prevState.value, progressArr],
            length: sentences.length,
          }));
        }

        //All loading done, push blobs to store
        dispatch({
          type: 'setAudioBlobs',
          payload: { blobs: audios },
        });

        dispatch({
          type: 'setConversationLength',
          payload: { length: sentences.length },
        });

        dispatch({
          type: 'setNpcAudioBlobs',
          payload: { blobs: npcAudio },
        });

        dispatch({
          type: 'setConversationData',
          payload: {convData : {...data, lesson, location}}
        })

        //Push sentences to context for lesson
        setConvContext((prevState) => ({
          ...prevState,
          sentences: sentences,
        }));
      } catch (error) {
        // console.warn(error)
        setError(error);
      }
    }

    if (data !== null) {
      setError('');
      loadData(data);
    }
  }, [data]);

  useEffect(() => {
    if (token) {
      setConvContext((prevState) => {
        return {
          ...prevState,
          microsoftToken: token,
        };
      });
    }
  }, [token]);

  const launchPreteach = () => {
    const url = `${apiUrl}/conversation/speak/${sceneID}/1/${1}?format=mp3`;
    // console.log(`conversation_button_${props.id}: ` + url)
    const fetchOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    dispatch({
      type: 'activatePreteach',
    });

    setActiveScreen('loading');
    preloadPreteachAudio({ url: url, options: fetchOptions });
  };

  let content = '';

  const jumpToConv = () => {
    if (useSpeechSDK()) {
      //Gets around Safaris autoplay policy
      document.getElementById('main_audio_player').load();
      document.getElementById('main_audio_player').play();
    } else {
      dispatch({
        type: 'playNpcAudio',
      });
    }

    setActiveScreen('conversation');
  };

  if (activeScreen === 'landing') {
    let parrotText = translate('conversation-welcome');
    let btnText = translate('begin-conversation');
    let extraContent;

    if( isTutorial ) {
      btnText = translate('start')
      let helpText;
      if( parseInt(lesson) === 1 ) {
        parrotText = translate('tutorial-lesson-1');
        helpText = translate('practise-each-part');
      }
      if( parseInt(lesson) === 2 ) {
        parrotText = translate('tutorial-lesson-2');
        helpText = translate('choose-correct-order')
      }
      
      if( parseInt(lesson) === 3 ) {
        parrotText = translate('tutorial-lesson-3');
        helpText = translate('have-a-conversation');
      }

      extraContent = 
        <div className='conversation__info-box'>
          <div className='info-box__header'>
            <img src={parrot} alt="Polly's tip" title="Polly's tip" className='info-box__icon' />
            <div className='info-box__content'>
              <p className='info-box__tip'>{translate('tutorial-title', {lesson: lesson})}</p> 
              <p className='info-box__prefix'>{helpText}</p> 
            </div>
          </div>
          <div className='progress-container'>
            <div className='progress-bar' style={{width: `${(parseInt(lesson) / 3) * 100}%`}}></div>
          </div>
        </div>
    }
    content = (
      <div className="conversation-intro">
        {extraContent}
        <ParrotBox
          text={parrotText}
          bitesize={false}
        />
        {! isTutorial && <button
          className="button button--alt"
          onClick={() => {
            launchPreteach();
          }}
          style={{fontSize: '1rem'}}
        >
          {translate('listen-preteach')}
        </button>}
        {! isTutorial && <span className='divide'>OR</span>}
        {canSkip && (
          <button
            className="button button--success preteach-skip"
            onClick={() => {
              jumpToConv();
              gaEventTracker('Pre-Teach Skipped', `${location} ${data.sceneName} ${lesson}`)
            }}
            onTouchStart={jumpToConv}
          >
            {btnText}
          </button>
        )}
      </div>
    );
  }

  if (activeScreen === 'loading') {
    content = <Loader>{translate('loading-conversation')}</Loader>;
  }

  if (activeScreen === 'preteach') {
    /* TODO: Preteach will also require objectivename, level, scene name and 
      lesson number to be passed to the component for GA purposes */
    content = (
      <Preteach location={location} completePreteach={completePreteach} />
    );
  }

  if (activeScreen === 'conversation') {
    content = <WholeConversation returnToStart={returnToStart} />;
  }

  if (error) {
    content = <div className='load-error'>
      <p className='load-error__title'>{translate('error-loading-conversation')}</p>
      <p className='load-error__error'>
        <strong>{translate('error-details')}</strong>
        {error}
      </p>
    </div>
  }

  useEffect(() => {
    if (audioData !== null) {
      setActiveScreen('preteach');
    }

    if (audioError !== null) {
      console.warn('failed to load audio');
    }
  }, [audioData, audioError]);

  useEffect(() => {
    if (activeScreen === 'preteach' || activeScreen === 'conversation') {
      updateUserProgress(activeScreen === 'preteach' ? true : false);
    }
  }, [activeScreen]);

  //Error catchers
  //Remove loader if errors
  useEffect(() => {
    if (error) {
      setIsFetching(false);
    }

    if (fetchErrors) {
      setError(fetchErrors);
    }
  }, [error, fetchErrors]);

  return (
    <ConvContext.Provider value={conversationValue}>
      <section className="section section__conversation">
        <div className="container">
          <SpeechRecognition />
          <MainAudioPlayer playerReady={npcPlayerReady} />
          <ButtonAudioPlayer />
          {isFetching ? (
            <Loader progress={progressValue}>{isTutorial ? translate('loading-tutorial') : translate('loading-conversation')}</Loader>
          ) : (
            <Fragment>{content}</Fragment>
          )}
        </div>
      </section>
    </ConvContext.Provider>
  );
};

export default Conversation;
